import { Link, useRouter, useSearch } from "@tanstack/react-router";
import { Route } from "@/routes/__root";
import { omit } from "lodash";
import {
  V2Dialog,
  VisuallyHidden,
  DialogTitle,
  DialogDescription,
  DialogContent,
} from "web-design-system";
import { useAuthContext } from "@/providers/auth-provider";
import { imageAssetUrl } from "@/utils/url";
import { useDistributorProfile } from "creso-core/hooks";
import { isEmployeeUser } from "creso-core/utils";
import { ExternalLinkIcon } from "lucide-react";
import { ClientLeadAccountForm } from "./client-account-form";
import { ClientLeadAccountUpdateForm } from "./update-lead-form";

export type AddClientDialogActions = "create-account" | "update";

export function NewClientImageComponent() {
  const { user, employee } = useAuthContext();
  const { data: profile } = useDistributorProfile();
  const isEmployee = isEmployeeUser(user.user_type);
  const nriOnboardingUrl = `https://onboarding.creso.in/?distributor_id=${profile?.arn_code?.replace(/^ARN-/, "") ?? ""}${isEmployee && employee?.id ? `&employee_id=${employee?.id}` : ""}`;

  return (
    <div className="flex-col w-80 justify-between flex p-6">
      <img
        src={imageAssetUrl("onboarding-v2.png")}
        alt="client-status"
        className="aspect-auto max-w-80"
        height={480}
      />
      <div className="w-full relative flex flex-col px-4 py-3 gap-0.5 rounded-lg border border-border bg-secondary">
        <Link
          to={nriOnboardingUrl}
          className="absolute top-0 left-0 size-full"
          target="_blank"
          rel="noreferrer"
        />
        <div className="flex flex-row justify-between">
          <span className="font-semibold text-xs">
            Onboard non-individual clients
          </span>
          <ExternalLinkIcon className="size-4" />
        </div>
        <span className="text-muted-foreground text-xs">
          HUF or Pvt account active <br /> within 3 hours
        </span>
      </div>
    </div>
  );
}

export function ClientLeadDialog() {
  const router = useRouter();

  const { client_id, action } = useSearch({
    from: Route.id,
  });

  const handleOpenChange = (open: boolean) => {
    if (open) return;
    router.navigate({
      to: ".",
      search: (prev) => omit(prev, ["modal", "client_id", "action"]),
      replace: true,
    });
  };

  const closeDialog = () => handleOpenChange(false);

  return (
    <V2Dialog defaultOpen onOpenChange={handleOpenChange}>
      <VisuallyHidden>
        <DialogTitle>Client Onboarding</DialogTitle>
        <DialogDescription id="add-client">Client Onboarding</DialogDescription>
      </VisuallyHidden>
      <DialogContent
        onInteractOutside={() => {
          router.invalidate();
        }}
        onOpenAutoFocus={(e) => e.preventDefault()}
        className="h-150 max-w-170 rounded-lg w-full p-0"
      >
        <div className="flex size-full">
          <div className="flex rounded-l-lg bg-linear-to-b from-secondary to-muted">
            <NewClientImageComponent />
          </div>
          <div className="flex-1">
            {client_id ? (
              <ClientLeadAccountUpdateForm
                action={action as AddClientDialogActions}
                id={client_id}
                onClose={closeDialog}
              />
            ) : (
              <ClientLeadAccountForm onClose={closeDialog} />
            )}
          </div>
        </div>
      </DialogContent>
    </V2Dialog>
  );
}
